import {
  Accordion,
  Alert,
  Button,
  Col,
  Container,
  Grid,
  Group,
  Select,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { ReactNode, useEffect } from 'react';
import { AlertCircle, Filter as FilterIcon } from 'tabler-icons-react';
import {
  BusinessTypesCode,
  BusinessTypesText,
} from '../../../enums/business-type';
import { MerchantOnboardingStatus } from '../../../enums/merchant-onboarding-status';
import { MerchantFilters } from '../../../type/filter.types';

interface Props {
  setFilter: React.Dispatch<React.SetStateAction<MerchantFilters>>;
  filter: MerchantFilters;
  isLoading: boolean;
}

const ResponsiveCol = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col span={12} sm={6} md={3}>
      {children}
    </Grid.Col>
  );
};

const MerchantFilter = ({ setFilter, isLoading, filter }: Props) => {
  const form = useForm({
    initialValues: {
      onboardingStatus: 'ALL',
      isActive: 'ALL',
      businessType: 'ALL',
    },
  });

  useEffect(() => {
    form.setValues({
      onboardingStatus: filter.onboardingStatus,
      isActive: filter.isActive,
      businessType: filter.businessType,
    });
  }, []);

  const onboardStatusData = [
    { value: 'ALL', label: 'All' },
    { value: MerchantOnboardingStatus.NEW, label: 'New' },
    { value: MerchantOnboardingStatus.APPROVED, label: 'Approved' },
    { value: MerchantOnboardingStatus.PENDING, label: 'Pending' },
    { value: MerchantOnboardingStatus.DECLINED, label: 'Declined' },
  ];
  const isActiveData = [
    { value: 'ALL', label: 'All' },
    { value: 'DISABLED', label: 'Disabled' },
    { value: 'ENABLED', label: 'Enabled' },
  ];
  const businessTypeData = [
    { value: 'ALL', label: 'All' },
    { value: BusinessTypesCode.STARTER, label: BusinessTypesText.STARTER },
    {
      value: BusinessTypesCode.SOLE_PROPRIETOR,
      label: BusinessTypesText.SOLE_PROPRIETOR,
    },
    {
      value: BusinessTypesCode.PARTNERSHIP,
      label: BusinessTypesText.PARTNERSHIP,
    },
    {
      value: BusinessTypesCode.CORPORATION,
      label: BusinessTypesText.CORPORATION,
    },
  ];

  const handleSubmit = (values: typeof form.values) => {
    const { isActive, onboardingStatus, businessType } = values;
    setFilter((prev) => {
      return { ...prev, onboardingStatus, isActive, businessType };
    });
  };

  const clearFilters = () => {
    form.setValues({
      onboardingStatus: 'ALL',
      isActive: 'ALL',
      businessType: 'ALL',
    });
    setFilter((prev) => {
      return {
        ...prev,
        onboardingStatus: 'ALL',
        isActive: 'ALL',
        businessType: 'ALL',
      };
    });
  };

  return (
    <form
      className='transactions-filter'
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <Accordion
        icon={<FilterIcon size={20} strokeWidth={1} color={'black'} />}
        offsetIcon={false}
        disableIconRotation
      >
        <Accordion.Item
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Filter
            </div>
          }
        >
          <Container pb='md' ml={0} fluid>
            <Grid>
              <ResponsiveCol>
                <Select
                  label='Onboarding Status'
                  data={onboardStatusData}
                  onChange={(value: string) =>
                    form.setFieldValue('onboardingStatus', value)
                  }
                  value={form.values.onboardingStatus}
                />
              </ResponsiveCol>
              <ResponsiveCol>
                <Select
                  label='Account Status'
                  data={isActiveData}
                  onChange={(value: string) =>
                    form.setFieldValue('isActive', value)
                  }
                  value={form.values.isActive.toString()}
                />
              </ResponsiveCol>
              <ResponsiveCol>
                <Select
                  label='Business type'
                  data={businessTypeData}
                  onChange={(value: string) =>
                    form.setFieldValue('businessType', value)
                  }
                  value={form.values.businessType.toString()}
                />
              </ResponsiveCol>
              {form.values.isActive === 'DISABLED' && (
                <Col>
                  <Alert
                    icon={<AlertCircle size={16} />}
                    title='Note'
                    color='red'
                    variant='light'
                  >
                    Onboarding Status filter is ignored when Account Status
                    filter is set to "Disabled"
                  </Alert>
                </Col>
              )}
            </Grid>
            <Group align='center' mt='lg' spacing='xs'>
              <Button
                type='submit'
                disabled={isLoading}
                className='alternative-mantine-disable'
              >
                Apply
              </Button>
              <Button
                onClick={clearFilters}
                variant='subtle'
                color='gray'
                px='sm'
                disabled={isLoading}
              >
                Clear Filters
              </Button>
            </Group>
          </Container>
        </Accordion.Item>
      </Accordion>
    </form>
  );
};

export default MerchantFilter;
